import apiCall from "./api";

const two = '2-digit';

/**
 * Converts a UTC timestamp to a specific timezone and formats it.
 * @param timestamp - The UTC timestamp to convert.
 * @param timezone - The timezone to convert the timestamp to.
 * @returns The converted time as a string.
 */
export const convertToLocalTimezone = (timestamp: any, timezone: any): string => {
    const success = (() => {
        try {
            return new Intl.DateTimeFormat('en-IN', {
                year: 'numeric', month: two, day: two, hour: two, minute: two, hour12: false, timeZone: timezone,
            }).format(new Date(timestamp * 1000));
        } catch {
            return 'Invalid timezone';
        };
    });
    return (!timestamp || !timezone ? '-' : isNaN(new Date(timestamp * 1000).getTime())) ? '-' : success();
};

/**
 * Converts a UTC timestamp to a human-readable format.
 * @param utcTimestamp - The UTC timestamp to convert.
 * @returns The converted time as a string.
 */
export const convertUTCToReadableFormat = (utcTimestamp: any): string => {
    const success = () => (new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: two, day: two, hour: two, minute: two, hour12: false, timeZone: 'UTC'
    }).format(new Date(utcTimestamp * 1000)));
    return (!utcTimestamp && utcTimestamp !== 0) ? '-' : isNaN(new Date(utcTimestamp * 1000).getTime()) ? '-' : success();
};

/**
 * Converts a UTC timestamp to a human-readable format.
 * @param utcTimestamp - The UTC timestamp to convert.
 * @returns The converted time as a string.
 */
export const convertUTCToHumanReadableFormat = (utcTimestamp: number): string => {
    const success = () => new Date(utcTimestamp * 1000).toLocaleString('en-US', {
        year: 'numeric', month: two, day: two, hour: two, minute: two, hour12: false,
    });
    return utcTimestamp ? success() : '-';
};

/**
 * Formats a UNIX timestamp to 'Y-m-d H:i:s' format.
 * @param {any} timestamp - The UNIX timestamp to format.
 * @returns {string} The formatted date string.
 * @throws {Error} If the input timestamp is not a valid number.
 */
export function convertToExcelFormat(timestamp: any) {
    const date = new Date(timestamp * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Retrieves the value of a cookie with the specified name.
 *
 * This function searches through the `document.cookie` string for a cookie that
 * matches the given name. If a match is found, the value of the cookie is returned;
 * otherwise, `undefined` is returned.
 *
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string | undefined} - The value of the cookie if found, or `undefined` if not found.
 *
 * @example
 * // Assume document.cookie is "user=JohnDoe; theme=dark"
 * const user = getCookie('user'); // "JohnDoe"
 * const theme = getCookie('theme'); // "dark"
 * const session = getCookie('session'); // undefined
 */
export function getCookie(name: string): string | undefined {
    let matches = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + "=([^;]*)"));
    return matches ? decodeURIComponent(matches[1]) : undefined;
};

/**
 * Updates the status of a user based on their email address.
 * @param userEmail - The email address of the user whose status needs to be updated.
 * @returns Promise<void>
 */
export const updateUserStatus = async (userEmail: string): Promise<void> => {
    if (!userEmail) {
        console.error('Error: User email is required.');
        return;
    };
    try {
        const response = await apiCall('/users/api/updateUserStatus', { user_email: userEmail });
        if (!response) {
            console.warn('Failed to update user status: No response received from API.');
            return;
        };
        console.log('User status updated successfully:', response);
    } catch (error) {
        console.error('An unexpected error occurred while updating user status:', (error as Error).message);
    };
};

export const getExportedData = async (url: string, condition: object) => {
    try {
        const { status, data } = await apiCall(url, condition);
        if (status === 'success') {
            return data;
        };
    } catch (error) {
        return error;
    };
};

export const convertDateToHumanReadableFormat = (utcTimestamp: any): string => {
    const success = (() => {
        try {
            return new Date(utcTimestamp).toLocaleString('en-US', {
                year: 'numeric', month: two, day: two, hour: two, minute: two, hour12: false, timeZone: 'UTC'
            });
        } catch {
            return 'Invalid timezone';
        };
    });
    return !utcTimestamp ? '-' : isNaN(new Date(utcTimestamp).getTime()) ? '-' : success();
};

export function convertToUTC(dateString) {
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            throw new Error('Invalid date format');
        };
        return date.toUTCString()
    } catch (error) {
        return { error: 'Failed to convert date: ' + error.message };
    };
};

export function convertToUTCMoment(dateString) {
    const moment = require('moment');
    try {
        const date = moment(dateString);
        // Check if date is valid
        if (!date.isValid()) {
            throw new Error('Invalid date format');
        };
        return date.utc().format('MM/DD/YYYY HH:mm [GMT]')
    } catch (error) {
        return moment(dateString).format('MM/DD/YYYY HH:mm [GMT]')
    };
};

export const popularTimezones = [
    "US/Eastern",           // Eastern (USA)
    "US/Central",           // Central (USA)
    "US/Pacific",           // Pacific (USA)
    "US/Mountain",          // Mountain (USA)
    "US/Alaska",            // Alaska
    "US/Hawaii",            // Hawaii
    "Europe/London",        // United Kingdom
    "Europe/Paris",         // France
    "Europe/Berlin",        // Germany
    "Europe/Moscow",        // Russia
    "Europe/Istanbul",      // Turkey
    "Asia/Tokyo",           // Japan
    "Asia/Singapore",       // Singapore
    "Asia/Shanghai",        // China
    "Asia/Seoul",           // Korea
    "Asia/Dubai",           // United Arab Emirites
    "Asia/Kolkata",         // India
    "Asia/Bangkok",         // Thailand
    "Asia/Kuala_Lumpur",    // Malaysia
    "Asia/Manila",          // Philippines
    "Australia/Sydney",     // Australian Eastern
    "Australia/Perth",      // Australian Western
    "Australia/Darwin",     // Australian Central
    "Pacific/Auckland",     // New Zealand
    "Africa/Cairo",         // Egypt
    "Africa/Johannesburg",  // South Africa
    "Africa/Lagos",         // Nigeria
    "Africa/Nairobi",       // Kenya
    "America/Sao_Paulo",    // Brazil
    "America/Buenos_Aires", // Argentina
    "America/Mexico_City",  // Mexico
    "America/Bogota",       // Colombia
    "America/Lima",         // Peru
    "America/Santiago",     // Chile
    "Canada/Eastern",       // Eastern Canada
    "Canada/Pacific",       // Pacific Canada
    "Atlantic/Reykjavik",   // Iceland
    "Europe/Dublin",        // Ireland
    "Europe/Rome",          // Italy
    "Europe/Madrid",        // Spain
    "Asia/Jerusalem",       // Israel
    "Asia/Tehran",          // Iran
    "Asia/Riyadh",          // Saudi Arabia
    "Asia/Vladivostok",     // Vladivostok
    "Pacific/Honolulu",     // Hawaii
    "Indian/Maldives",      // Maldives
    "US/Arizona",           // Arizona (No Daylight Saving)
    "Pacific/Noumea",       // New Caledonia
    "Asia/Qatar",           // Qatar
    "Asia/Yerevan"          // Armenia
];
